import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import { HiOutlineClipboardList } from "react-icons/hi";
import {
  PaDataTable,
  PaEmptyState,
  PaFilterBar,
  PaPageHeader,
  PaPagination,
  PaStatusBadge,
} from "components/platform-admin";
import PlatformAdminLayout from "pages/PlatformAdmin/layout/PlatformAdminLayout";
import { platformRoleAccess } from "helpers/RolesPermission";
import { getPaginationObj } from "Utils/utils";
import { usePlatformAdmin } from "context/PlatformAdminContext";
import GhostStartModal from "pages/PlatformAdmin/Ghost/GhostStartModal";
import {
  canGhostUser,
  resolveGhostTargetSpId,
} from "pages/PlatformAdmin/Ghost/ghostSession.utils";
import {
  lockPlatformUser,
  resetPlatformUserMfa,
  resetPlatformUserPassword,
  revokePlatformUserSessions,
  searchPlatformUsers,
  unlockPlatformUser,
  updatePlatformUserEmail,
} from "store/services/platformAdmin.services";
import ChangeUserEmailModal from "./ChangeUserEmailModal";
import ResetUserPasswordModal from "./ResetUserPasswordModal";
import UserActionModal from "./UserActionModal";
import UserSupportDrawer from "./UserSupportDrawer";
import {
  canRunUserSearch,
  DEFAULT_USER_SEARCH_FILTERS,
  formatAffiliationType,
  formatUserTypeLabel,
  MIN_USER_SEARCH_LENGTH,
} from "./userSearch.utils";
import {
  ACCOUNT_ISSUE_LABELS,
  ACCOUNT_ISSUE_VARIANTS,
  analyzeUserAccount,
  buildRestoreLoginSteps,
  canRestoreLoginAccess,
  USER_ACCOUNT_STATE_FILTERS,
} from "./userSupport.utils";

const formatTimestamp = (value) => {
  if (!value) {
    return "—";
  }

  const timestamp = moment(value);
  if (!timestamp.isValid()) {
    return "—";
  }

  return (
    <time dateTime={value} title={timestamp.format("D MMM YYYY, h:mm a")}>
      {timestamp.fromNow()}
    </time>
  );
};

const USER_ACTION_CONFIG = {
  lock: {
    title: "Lock account",
    description: "Disable Cognito login for this user.",
    confirmLabel: "Lock account",
    destructive: true,
    successMessage: "User locked",
  },
  unlock: {
    title: "Unlock account",
    description: "Re-enable Cognito login for this user.",
    confirmLabel: "Unlock account",
    destructive: false,
    successMessage: "User unlocked",
  },
  revoke: {
    title: "Revoke sessions",
    description: "Force global sign-out for this user.",
    confirmLabel: "Revoke sessions",
    destructive: true,
    successMessage: "Sessions revoked",
  },
  mfa: {
    title: "Reset MFA",
    description: "Clear Cognito MFA preferences for this user.",
    confirmLabel: "Reset MFA",
    destructive: true,
    successMessage: "MFA reset",
  },
  restore_login: {
    title: "Restore login access",
    description:
      "Unlock (if needed), revoke all sessions, and reset MFA so the user can sign in fresh.",
    confirmLabel: "Restore login access",
    destructive: true,
    successMessage: "Login access restored",
  },
};

const UserSearch = () => {
  const canSearch = platformRoleAccess("platform.user.search");
  const canResetPassword = platformRoleAccess("platform.user.password_reset");
  const canUpdateEmail = platformRoleAccess("platform.user.email_update");
  const canLockUser = platformRoleAccess("platform.user.lock");
  const canRevokeSessions = platformRoleAccess("platform.session.revoke");
  const canResetMfa = platformRoleAccess("platform.user.mfa_reset");
  const canStartGhost = platformRoleAccess("platform.ghost.start");
  const { startGhost, ghostSubmitting } = usePlatformAdmin();

  const supportPermissions = useMemo(
    () => ({
      passwordReset: canResetPassword,
      emailUpdate: canUpdateEmail,
      lock: canLockUser,
      revoke: canRevokeSessions,
      mfa: canResetMfa,
      ghost: canStartGhost,
    }),
    [canResetPassword, canUpdateEmail, canLockUser, canRevokeSessions, canResetMfa, canStartGhost]
  );

  const [ghostTarget, setGhostTarget] = useState(null);
  const [supportUser, setSupportUser] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState(DEFAULT_USER_SEARCH_FILTERS);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(getPaginationObj());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [emailUser, setEmailUser] = useState(null);
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [userAction, setUserAction] = useState(null);
  const [userActionSubmitting, setUserActionSubmitting] = useState(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(searchInput.trim());
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    setFilters((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [searchTerm, filters.accountState]);

  useEffect(() => {
    if (!canSearch) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadUsers = async () => {
      setLoading(true);
      setError(null);

      try {
        const payload = await searchPlatformUsers({
          search: searchTerm,
          accountState: filters.accountState,
          page: filters.page,
          perPage: filters.perPage,
        });

        if (!cancelled) {
          const users = payload?.users;
          setRows(Array.isArray(users?.data) ? users.data : []);
          setPagination(getPaginationObj(users, filters));
        }
      } catch (err) {
        if (!cancelled) {
          setRows([]);
          setPagination(getPaginationObj());
          setError(err?.message || "Failed to load users");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadUsers();

    return () => {
      cancelled = true;
    };
  }, [canSearch, searchTerm, filters.page, filters.perPage, filters.accountState]);

  const reloadUsers = async () => {
    const payload = await searchPlatformUsers({
      search: searchTerm,
      accountState: filters.accountState,
      page: filters.page,
      perPage: filters.perPage,
    });
    const users = payload?.users;
    const nextRows = Array.isArray(users?.data) ? users.data : [];
    setRows(nextRows);
    setPagination(getPaginationObj(users, filters));

    if (supportUser) {
      const refreshed = nextRows.find((row) => row.id === supportUser.id);
      if (refreshed) {
        setSupportUser(refreshed);
      }
    }
  };

  const handleClear = () => {
    setSearchInput("");
    setSearchTerm("");
    setFilters(DEFAULT_USER_SEARCH_FILTERS);
    setError(null);
  };

  const handleAccountStateChange = (accountState) => {
    setFilters((prev) => ({
      ...prev,
      accountState: prev.accountState === accountState ? "" : accountState,
      page: 1,
    }));
  };

  const openUserAction = (type, user) => {
    const config = USER_ACTION_CONFIG[type];
    if (!config) {
      return;
    }

    setUserAction({
      type,
      user,
      ...config,
    });
  };

  const handleSupportAction = (actionId, user) => {
    if (actionId === "reset_password") {
      setSupportUser(null);
      setResetUser(user);
      return;
    }

    if (actionId === "change_email") {
      setSupportUser(null);
      setEmailUser(user);
      return;
    }

    if (actionId === "ghost") {
      setSupportUser(null);
      setGhostTarget(user);
      return;
    }

    openUserAction(actionId, user);
  };

  const handleResetPassword = async (password) => {
    if (!resetUser?.email) {
      return;
    }

    setResetSubmitting(true);

    try {
      const message = await resetPlatformUserPassword(resetUser.email, password);
      toast.success(message || "Password updated successfully!");
      setResetUser(null);
    } catch (err) {
      toast.error(err?.message || "Failed to reset password");
    } finally {
      setResetSubmitting(false);
    }
  };

  const handleChangeEmail = async ({ email, reason }) => {
    if (!emailUser?.id) {
      return;
    }

    setEmailSubmitting(true);

    try {
      await updatePlatformUserEmail(emailUser.id, email, reason);
      toast.success("Login email updated");
      setEmailUser(null);
      await reloadUsers();
    } catch (err) {
      toast.error(err?.message || "Failed to update email");
    } finally {
      setEmailSubmitting(false);
    }
  };

  const handleStartGhost = async ({ reason, readOnly = false, spId = null }) => {
    if (!ghostTarget) {
      return;
    }

    const resolvedSpId = spId ?? resolveGhostTargetSpId(ghostTarget);
    if (!resolvedSpId) {
      toast.error("Select a tenant affiliation before starting ghost mode.");
      return;
    }

    const affiliation =
      ghostTarget.affiliations?.find((item) => item.sp_id === resolvedSpId) || null;

    try {
      await startGhost({
        userId: ghostTarget.id,
        reason,
        readOnly,
        spId: resolvedSpId,
        actingUser: ghostTarget,
        tenantName: affiliation?.sp_name,
        tenantFlags: {
          suspended: affiliation?.tenant_status === "suspended",
          read_only: Boolean(affiliation?.read_only),
        },
      });
      setGhostTarget(null);
    } catch (err) {
      toast.error(err?.message || "Failed to start ghost session");
    }
  };

  const runRestoreLoginAccess = async (user, reason) => {
    const account = analyzeUserAccount(user);

    if (account.isLocked) {
      await unlockPlatformUser(user.id, reason);
    }

    await revokePlatformUserSessions(user.id, reason);
    await resetPlatformUserMfa(user.id, reason);
  };

  const handleUserAction = async (reason) => {
    if (!userAction) {
      return;
    }

    setUserActionSubmitting(true);

    try {
      if (userAction.type === "restore_login") {
        await runRestoreLoginAccess(userAction.user, reason);
      } else {
        const handlers = {
          lock: lockPlatformUser,
          unlock: unlockPlatformUser,
          revoke: revokePlatformUserSessions,
          mfa: resetPlatformUserMfa,
        };
        await handlers[userAction.type](userAction.user.id, reason);
      }

      toast.success(userAction.successMessage);
      setUserAction(null);
      await reloadUsers();
    } catch (err) {
      toast.error(err?.message || "Action failed");
    } finally {
      setUserActionSubmitting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: "name",
        label: "Name",
        render: (row) => (
          <div className="font-medium text-gray-900">{row.name || "—"}</div>
        ),
      },
      {
        key: "email",
        label: "Email",
      },
      {
        key: "user_type",
        label: "Type",
        render: (row) => (
          <PaStatusBadge label={formatUserTypeLabel(row.user_type)} variant="info" />
        ),
      },
      {
        key: "account",
        label: "Account",
        render: (row) => {
          const account = analyzeUserAccount(row);

          if (account.issues.length === 0) {
            return (
              <PaStatusBadge label={row.status ? "Healthy" : "Locked"} variant="success" />
            );
          }

          return (
            <div className="flex flex-wrap gap-1.5">
              {account.issues.map((issue) => (
                <PaStatusBadge
                  key={issue}
                  label={ACCOUNT_ISSUE_LABELS[issue] || issue}
                  variant={ACCOUNT_ISSUE_VARIANTS[issue] || "neutral"}
                />
              ))}
            </div>
          );
        },
      },
      {
        key: "affiliations",
        label: "Tenants",
        render: (row) => {
          const affiliations = row.affiliations || [];

          if (affiliations.length === 0) {
            return "—";
          }

          return (
            <ul className="space-y-2">
              {affiliations.map((affiliation) => (
                <li key={`${row.id}-${affiliation.sp_id}`} className="text-sm">
                  <Link
                    to={`/tenants/${affiliation.sp_id}`}
                    className="font-medium text-indigo-600 hover:text-indigo-700"
                  >
                    {affiliation.sp_name}
                  </Link>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <PaStatusBadge
                      label={formatAffiliationType(affiliation.affiliation_type)}
                      variant="neutral"
                    />
                    {affiliation.tenant_status ? (
                      <PaStatusBadge
                        label={affiliation.tenant_status}
                        variant={
                          affiliation.tenant_status === "suspended"
                            ? "danger"
                            : "success"
                        }
                      />
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          );
        },
      },
      {
        key: "last_login_at",
        label: "Last login",
        render: (row) => formatTimestamp(row.last_login_at),
      },
      {
        key: "actions",
        label: "Actions",
        headerClassName: "text-right",
        cellClassName: "text-right",
        render: (row) => (
          <div className="inline-flex items-center justify-end gap-1">
            <button
              type="button"
              onClick={() => setSupportUser(row)}
              title="Open account support toolkit"
              className="inline-flex items-center gap-1 rounded-lg bg-indigo-50 px-2.5 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <HiOutlineClipboardList className="h-4 w-4" aria-hidden="true" />
              Support
            </button>
          </div>
        ),
      },
    ],
    []
  );

  if (!canSearch) {
    return (
      <PlatformAdminLayout>
        <PaPageHeader title="User search" subtitle="Find users across tenants by name or email" />
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <PaEmptyState
            title="Access restricted"
            message="You don't have access to this area"
            description="You need platform.user.search to search users."
          />
        </div>
      </PlatformAdminLayout>
    );
  }

  return (
    <PlatformAdminLayout>
      <PaPageHeader
        title="User search"
        subtitle="Resolve account support requests with guided playbooks"
      />

      <PaFilterBar
        className="mb-4"
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Search by name or email (optional, min 3 characters)"
        onClear={searchInput || filters.accountState ? handleClear : undefined}
        resultCount={pagination.total}
      />

      <div className="mb-4 flex flex-wrap gap-2">
        {USER_ACCOUNT_STATE_FILTERS.map((filter) => {
          const active = filters.accountState === filter.id;

          return (
            <button
              key={filter.id}
              type="button"
              onClick={() => handleAccountStateChange(filter.id)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                active
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {filter.label}
            </button>
          );
        })}
      </div>

      {!canRunUserSearch(searchTerm) && searchInput ? (
        <p className="mb-4 text-sm text-gray-500">
          Enter at least {MIN_USER_SEARCH_LENGTH} characters to filter results.
        </p>
      ) : null}

      {canRestoreLoginAccess(supportPermissions) ? (
        <p className="mb-4 text-sm text-gray-500">
          Open <span className="font-medium text-gray-700">Support</span> on a user to run
          playbooks for login issues, password resets, MFA, and ghost verification.
        </p>
      ) : null}

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <PaDataTable
        columns={columns}
        rows={rows}
        rowKey="id"
        isLoading={loading}
        emptyState={
          <PaEmptyState
            title="No users found"
            message={
              searchInput || filters.accountState
                ? "Try a different search or filter"
                : "No users are available to display"
            }
          />
        }
      />

      <PaPagination
        page={filters.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        rows={rows}
        perPage={filters.perPage}
        loading={loading}
        onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
      />

      <UserSupportDrawer
        open={Boolean(supportUser)}
        onClose={() => setSupportUser(null)}
        user={supportUser}
        permissions={supportPermissions}
        onRunAction={handleSupportAction}
      />

      <ResetUserPasswordModal
        open={Boolean(resetUser)}
        onClose={() => setResetUser(null)}
        user={resetUser}
        submitting={resetSubmitting}
        onSubmit={handleResetPassword}
      />

      <ChangeUserEmailModal
        open={Boolean(emailUser)}
        onClose={() => setEmailUser(null)}
        user={emailUser}
        submitting={emailSubmitting}
        onSubmit={handleChangeEmail}
      />

      <UserActionModal
        open={Boolean(userAction)}
        onClose={() => setUserAction(null)}
        title={userAction?.title}
        description={
          userAction?.type === "restore_login" && userAction?.user
            ? `${USER_ACTION_CONFIG.restore_login.description} Steps: ${buildRestoreLoginSteps(userAction.user).join(" → ")}.`
            : userAction?.description
        }
        confirmLabel={userAction?.confirmLabel}
        destructive={userAction?.destructive}
        submitting={userActionSubmitting}
        onSubmit={handleUserAction}
      />

      <GhostStartModal
        open={Boolean(ghostTarget)}
        onClose={() => setGhostTarget(null)}
        title="Start ghost session"
        description="You will operate the portal as this user. Your access is limited to what they can do. All actions are audited."
        targetLabel={
          ghostTarget
            ? `${ghostTarget.name || "User"} (${ghostTarget.email})`
            : null
        }
        submitting={ghostSubmitting}
        allowReadOnly
        affiliations={
          ghostTarget?.affiliations?.length > 1 ? ghostTarget.affiliations : null
        }
        onSubmit={handleStartGhost}
      />
    </PlatformAdminLayout>
  );
};

export default UserSearch;
